// Calls for the Skill Builder player, mounted under `/studentBmd/*` in
// tutor-backend beside the diagnostics runtime.
//
// A Skill Builder is NOT an activity_pdf, so it does not go through the
// worksheet endpoints. It has its own table, its own S3 prefix and its own
// marking path.

import axios from "axios"
import { BASE_URL } from "../../global"

const OPEN_ENDPOINT = "studentBmd/skillbuilder"
const SUBMIT_ENDPOINT = "studentBmd/submitSkillbuilder"
const FILE_ENDPOINT = "studentBmd/skillbuilderFile"

function withAuth(authUser) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${authUser}`
}

/**
 * Where the PDF is and whether it can be marked at all.
 *
 * The check happens BEFORE the student is shown the worksheet on purpose: an
 * unreadable answer key means filling it in would score zero through no fault
 * of theirs, so it is better to refuse to open it.
 */
/**
 * Largest flattened copy we will attach to a submission. 0 disables the upload.
 *
 * CURRENTLY 0: the API gateway was returning 413 and losing students' work, and
 * its body limit is not something we can read or change from here. A size cap
 * would only be a guess at that limit; not sending the file at all is certain.
 *
 * This costs nothing functionally - the marked copy is regenerated server-side
 * from the stored answers, see the note at the append below. Raise it to
 * something under the gateway limit once that limit is known and confirmed.
 */
const SUBMIT_PDF_MAX_BYTES = 0

export async function openSkillbuilder(authUser, pkLessonActivityKey) {
  withAuth(authUser)
  const response = await axios.post(BASE_URL + OPEN_ENDPOINT, {
    pk_lesson_activity_key: pkLessonActivityKey,
  })
  return response.data
}

/**
 * Submits the typed answers for marking.
 *
 * Marking uses the TYPED VALUES, never the PDF bytes, so a browser that cannot
 * produce a flattened copy still submits successfully.
 *
 * `submitted_on` is SECONDS SPENT, not a timestamp, however much the name
 * suggests otherwise. The server rejects anything that looks like an epoch
 * value, and lesson-plan.js sums this column as total time.
 */
export async function submitSkillbuilder(authUser, payload) {
  withAuth(authUser)

  // Multipart, so the flattened PDF can ride along as the tutor's review copy.
  // The answers therefore travel as a JSON string; the server parses them back.
  const form = new FormData()
  form.append("pk_lesson_activity_key", payload.pk_lesson_activity_key)
  form.append("fk_lesson_key", payload.fk_lesson_key ?? "")
  form.append("fk_ses_key", payload.fk_ses_key ?? "")
  form.append("fk_sub_id", payload.fk_sub_id ?? "")
  form.append("submitted_on", payload.seconds_spent ?? 0)
  form.append("answers", JSON.stringify(payload.answers || {}))
  if (payload.question_id != null)
    form.append("question_id", payload.question_id)

  // Optional on purpose. Marking uses the typed answers, so a browser that
  // cannot flatten the form still submits successfully and is still marked.
  //
  // AND IT IS CAPPED. The flattened copy is the entire weight of this request -
  // the answers themselves are about sixty bytes - and the API gateway returns
  // 413 before Express ever sees the body. That defeated the "an upload must
  // never fail the submission" design, because the rejection happens upstream
  // of the middleware written to swallow it: the student saw "Could not submit"
  // and their work was lost.
  //
  // Nothing depends on these bytes. The marked copy the student and tutor
  // review is REGENERATED server-side from the original worksheet plus the
  // stored answers (fillWithStudentAnswers), so an attempt with no uploaded
  // copy reviews identically to one with it.
  if (payload.filledPdf) {
    const size = payload.filledPdf.size || 0
    if (SUBMIT_PDF_MAX_BYTES > 0 && size <= SUBMIT_PDF_MAX_BYTES) {
      form.append("pdfFile", payload.filledPdf, buildSubmissionFileName(payload))
    } else {
      // Worth seeing in the console: the submission still succeeds, but the
      // stored copy is missing and this says why.
      console.warn(
        `Skill builder copy not uploaded: ${size} bytes exceeds the ` +
          `${SUBMIT_PDF_MAX_BYTES} byte limit. The attempt is still submitted ` +
          `and marked.`,
      )
    }
  }

  const response = await axios.post(BASE_URL + SUBMIT_ENDPOINT, form, {
    headers: { "Content-Type": "multipart/form-data" },
  })
  return response.data
}

/**
 * A name that identifies the attempt without trusting the browser to be
 * unique. The server sanitises and prefixes it with a timestamp regardless.
 */
function buildSubmissionFileName(payload) {
  return `skillbuilder_${payload.pk_lesson_activity_key}_${Date.now()}.pdf`
}

/**
 * The worksheet PDF bytes, streamed through the API.
 *
 * Not fetched from the asset store directly: that needs a CORS rule on the
 * bucket, and a download manager extension will hijack any URL ending in .pdf.
 * Going through the API avoids both and reuses the student's existing auth.
 */
export async function fetchSkillbuilderPdf(authUser, pkLessonActivityKey) {
  withAuth(authUser)
  const response = await axios.post(
    BASE_URL + FILE_ENDPOINT,
    { pk_lesson_activity_key: pkLessonActivityKey },
    { responseType: "arraybuffer" }
  )
  return new Uint8Array(response.data)
}
